import { useEffect, useMemo, useState } from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import FormControl from '@mui/joy/FormControl'
import FormHelperText from '@mui/joy/FormHelperText'
import FormLabel from '@mui/joy/FormLabel'
import IconButton from '@mui/joy/IconButton'
import Input from '@mui/joy/Input'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Stack from '@mui/joy/Stack'
import Textarea from '@mui/joy/Textarea'
import Typography from '@mui/joy/Typography'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
    PROMO_PAGE_CTA_LABEL_MAX,
    PROMO_PAGE_FEATURE_SUBTITLE_MAX,
    PROMO_PAGE_FEATURE_TITLE_MAX,
    PROMO_PAGE_FEATURES_MAX,
    PROMO_PAGE_HEADING_MAX,
    PROMO_PAGE_ICONS,
    PROMO_PAGE_SUBHEADING_MAX,
    promoPageContentSchema,
    type PromoPageContent,
    type PromoPageEditorView,
    type PromoPageIcon,
} from '@numeo/types-main-mobile'
import { promoPageDeeplink } from '@numeo/types'
import { MOBILE_APP_SCHEME } from '@numeo/types-core'
import { apiErrorMessage, mobileHomeLayoutApi } from '../../../../api/mobileHomeLayout'
import { mobilePromoPagesApi } from '../../../../api/mobilePromoPages'
import { PROMO_PAGE_ICON_COMPONENTS } from './promoPageIcons'
import { PromoPagePreview } from './PromoPagePreview'

const EMPTY_PAGE: PromoPageContent = {
    heading: '',
    subheading: '',
    features: [{ icon: 'circle-check', title: '', subtitle: '' }],
    ctaLabel: 'Get Started',
    ctaUrl: '',
}

function readImageSize(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => {
            URL.revokeObjectURL(url)
            resolve({ width: img.naturalWidth, height: img.naturalHeight })
        }
        img.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error('That file could not be read as an image.'))
        }
        img.src = url
    })
}

interface Props {
    slug: string
    view: PromoPageEditorView | null
    onChanged: (view: PromoPageEditorView) => void
    notify: (message: string, color: 'success' | 'danger') => void
}

export function PromoPageEditor({ slug, view, onChanged, notify }: Props) {
    const [page, setPage] = useState<PromoPageContent>(view?.draft ?? view?.published ?? EMPTY_PAGE)
    const [busy, setBusy] = useState<'save' | 'publish' | 'unpublish' | 'upload' | 'rollback' | null>(null)

    useEffect(() => setPage(view?.draft ?? view?.published ?? EMPTY_PAGE), [slug, view])
    // Edits not yet saved as the draft. Publish saves them first, so what goes live is what the editor shows.
    const unsaved = useMemo(() => JSON.stringify(page) !== JSON.stringify(view?.draft ?? view?.published ?? EMPTY_PAGE), [page, view])

    const validation = useMemo(() => promoPageContentSchema.safeParse(page), [page])
    const problems = validation.success ? [] : validation.error.issues.map((i) => `${i.path.join('.') || 'page'}: ${i.message}`)
    const patch = (next: Partial<PromoPageContent>) => setPage((prev) => ({ ...prev, ...next }))
    const patchFeature = (index: number, next: Partial<PromoPageContent['features'][number]>) =>
        setPage((prev) => ({ ...prev, features: prev.features.map((f, i) => (i === index ? { ...f, ...next } : f)) }))

    async function run(kind: NonNullable<typeof busy>, fn: () => Promise<PromoPageEditorView>, done: string) {
        setBusy(kind)
        try {
            onChanged(await fn())
            notify(done, 'success')
        } catch (err) {
            notify(apiErrorMessage(err), 'danger')
        } finally {
            setBusy(null)
        }
    }

    async function uploadLogo(event: React.ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        setBusy('upload')
        try {
            const { width, height } = await readImageSize(file)
            const url = await mobileHomeLayoutApi.uploadPromoImage(file)
            patch({ logoImageUrl: url, logoAspectRatio: Number((width / height).toFixed(3)) })
        } catch (err) {
            notify(apiErrorMessage(err), 'danger')
        } finally {
            setBusy(null)
        }
    }

    const deeplink = promoPageDeeplink(slug)
    return (
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
            <Card sx={{ flex: 1, minWidth: 0, gap: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography level="title-lg">{slug}</Typography>
                    {view?.published ? (
                        <Chip color="success" variant="soft">
                            Published
                        </Chip>
                    ) : (
                        <Chip variant="soft">Not published</Chip>
                    )}
                    {view?.draft ? (
                        <Chip color="warning" variant="soft">
                            Unpublished changes
                        </Chip>
                    ) : null}
                </Stack>
                <FormControl>
                    <FormLabel>Deep link</FormLabel>
                    <Input
                        readOnly
                        value={deeplink}
                        endDecorator={
                            <Button size="sm" variant="plain" onClick={() => void navigator.clipboard.writeText(deeplink)}>
                                Copy
                            </Button>
                        }
                    />
                    <FormHelperText>Use it as a Home banner or Loads card link, or as the factoring "No" link in Config.</FormHelperText>
                </FormControl>

                <FormControl>
                    <FormLabel>Logo strip (optional)</FormLabel>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {page.logoImageUrl ? (
                            <Box component="img" src={page.logoImageUrl} alt="" sx={{ height: 32, bgcolor: '#000', borderRadius: 'sm', p: 0.5 }} />
                        ) : null}
                        <Button component="label" size="sm" variant="outlined" loading={busy === 'upload'}>
                            {page.logoImageUrl ? 'Replace' : 'Upload'}
                            <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => void uploadLogo(e)} />
                        </Button>
                        {page.logoImageUrl ? (
                            <Button size="sm" variant="plain" color="neutral" onClick={() => patch({ logoImageUrl: undefined, logoAspectRatio: undefined })}>
                                Remove
                            </Button>
                        ) : null}
                    </Stack>
                    <FormHelperText>Shown on a dark background, about 217×46 on a phone. Use a transparent PNG.</FormHelperText>
                </FormControl>

                <FormControl>
                    <FormLabel>Heading</FormLabel>
                    <Input
                        value={page.heading}
                        onChange={(e) => patch({ heading: e.target.value })}
                        slotProps={{ input: { maxLength: PROMO_PAGE_HEADING_MAX } }}
                    />
                </FormControl>
                <FormControl>
                    <FormLabel>Subheading</FormLabel>
                    <Textarea
                        minRows={2}
                        value={page.subheading}
                        onChange={(e) => patch({ subheading: e.target.value })}
                        slotProps={{ textarea: { maxLength: PROMO_PAGE_SUBHEADING_MAX } }}
                    />
                </FormControl>

                <Typography level="title-sm">
                    Features ({page.features.length}/{PROMO_PAGE_FEATURES_MAX})
                </Typography>
                {page.features.map((feature, index) => (
                    <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                        <Select
                            size="sm"
                            value={feature.icon}
                            onChange={(_, value) => value && patchFeature(index, { icon: value as PromoPageIcon })}
                            renderValue={(selected) => {
                                const Icon = selected ? PROMO_PAGE_ICON_COMPONENTS[selected.value as PromoPageIcon] : null
                                return Icon ? <Icon size={16} /> : null
                            }}
                            sx={{ width: 72 }}
                        >
                            {PROMO_PAGE_ICONS.map((icon) => {
                                const Icon = PROMO_PAGE_ICON_COMPONENTS[icon]
                                return (
                                    <Option key={icon} value={icon} label={icon}>
                                        <Icon size={16} /> {icon}
                                    </Option>
                                )
                            })}
                        </Select>
                        <Stack spacing={0.5} sx={{ flex: 1 }}>
                            <Input
                                size="sm"
                                placeholder="Title"
                                value={feature.title}
                                onChange={(e) => patchFeature(index, { title: e.target.value })}
                                slotProps={{ input: { maxLength: PROMO_PAGE_FEATURE_TITLE_MAX } }}
                            />
                            <Input
                                size="sm"
                                placeholder="Subtitle"
                                value={feature.subtitle}
                                onChange={(e) => patchFeature(index, { subtitle: e.target.value })}
                                slotProps={{ input: { maxLength: PROMO_PAGE_FEATURE_SUBTITLE_MAX } }}
                            />
                        </Stack>
                        <IconButton
                            size="sm"
                            disabled={page.features.length <= 1}
                            onClick={() => setPage((prev) => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }))}
                        >
                            <DeleteOutlineIcon />
                        </IconButton>
                    </Stack>
                ))}
                <Button
                    size="sm"
                    variant="outlined"
                    disabled={page.features.length >= PROMO_PAGE_FEATURES_MAX}
                    onClick={() => setPage((prev) => ({ ...prev, features: [...prev.features, { icon: 'circle-check', title: '', subtitle: '' }] }))}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    Add feature
                </Button>

                <Stack direction="row" spacing={1}>
                    <FormControl sx={{ width: 200 }}>
                        <FormLabel>Button label</FormLabel>
                        <Input
                            value={page.ctaLabel}
                            onChange={(e) => patch({ ctaLabel: e.target.value })}
                            slotProps={{ input: { maxLength: PROMO_PAGE_CTA_LABEL_MAX } }}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Button link</FormLabel>
                        <Input
                            placeholder={`https://… or ${MOBILE_APP_SCHEME}…`}
                            value={page.ctaUrl}
                            onChange={(e) => patch({ ctaUrl: e.target.value.trim() })}
                        />
                    </FormControl>
                </Stack>

                {problems.length ? (
                    <Typography level="body-sm" color="warning">
                        {problems.join(' · ')}
                    </Typography>
                ) : null}

                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                        disabled={!validation.success}
                        loading={busy === 'save'}
                        onClick={() => void run('save', () => mobilePromoPagesApi.saveDraft(slug, page), 'Draft saved')}
                    >
                        Save draft
                    </Button>
                    <Button
                        color="success"
                        disabled={unsaved ? !validation.success : !view?.draft}
                        loading={busy === 'publish'}
                        onClick={() =>
                            void run(
                                'publish',
                                async () => {
                                    if (unsaved) await mobilePromoPagesApi.saveDraft(slug, page)
                                    return mobilePromoPagesApi.publish(slug)
                                },
                                'Published'
                            )
                        }
                    >
                        Publish
                    </Button>
                    <Button
                        color="neutral"
                        variant="outlined"
                        disabled={!view?.published}
                        loading={busy === 'unpublish'}
                        onClick={() => void run('unpublish', () => mobilePromoPagesApi.unpublish(slug), 'Unpublished')}
                    >
                        Unpublish
                    </Button>
                </Stack>

                {view?.history.length ? (
                    <Box>
                        <Typography level="title-sm">History</Typography>
                        {view.history.map((v) => (
                            <Stack key={v.index} direction="row" spacing={1} alignItems="center">
                                <Typography level="body-sm">{`${new Date(v.publishedAt).toLocaleString()} · ${v.publishedBy}`}</Typography>
                                <Button
                                    size="sm"
                                    variant="plain"
                                    loading={busy === 'rollback'}
                                    onClick={() => void run('rollback', () => mobilePromoPagesApi.rollback(slug, v.index), 'Restored')}
                                >
                                    Restore
                                </Button>
                            </Stack>
                        ))}
                    </Box>
                ) : null}
            </Card>
            <PromoPagePreview page={page} />
        </Stack>
    )
}
